import {
  AiAdminView,
  AiFeedbackSaved,
  AiProviderTestResult,
  AiSpeechTestResult,
  AiStatus,
  AskDataAnswer,
  AskDataImageRecorded,
  AskDataResult,
  AskDataStarters,
  AskDataViewSaved,
  AssistantMailStatus,
  AuditLogPage,
  HelpConversation,
  HelpHandoffSent,
  HelpMessage,
  HelpReply,
  HelpSearchResults,
  HelpTopics,
  HelpVoiceTicket,
  SaveAiProvider,
  SimulateAssistantMail,
  SimulatedMailResult,
  TestAiProvider,
  AskDataTable,
  type AiFeedback,
  type AskDataExport,
  type AskDataExportFormat,
  type AskDataImageKind,
  type AskDataQuery,
  type AskView,
  type HelpHandoff,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { z } from 'zod';
import { request, requestDownload } from './client';

/**
 * The AI features (APP-048): which ones I can use, Admin › AI (SYS7), Help (AI4) and Ask the
 * data (RPT5). An API key is sent once, from the handler that saves it, and is never put in the
 * query cache, a mutation's variables, a toast or an error.
 */

export const aiKeys = {
  status: ['ai', 'status'] as const,
  admin: ['ai', 'admin'] as const,
  audit: (entityType: string) => ['ai', 'audit', entityType] as const,
  help: ['help'] as const,
  topics: (page: string) => ['help', 'topics', page] as const,
  conversation: ['help', 'conversation'] as const,
  ask: ['insights-ask'] as const,
  starters: ['insights-ask', 'starters'] as const,
  run: (query: AskDataQuery) => ['insights-ask', 'run', query] as const,
};

// ---- Admin › Sent mail: simulated email to the assistant (APP-071) ----------------------------

export const assistantMailKeys = { status: ['sent-mail', 'assistant'] as const };

/** Whether I can write to the assistant as someone here (test environments, named testers). */
export const assistantMailStatusQuery = queryOptions({
  queryKey: assistantMailKeys.status,
  queryFn: ({ signal }) =>
    request('/api/v1/admin/assistant-mail', { schema: AssistantMailStatus, signal }),
  staleTime: 30_000,
});

/** Writes as someone and waits for the assistant's answer (the server runs it at once). */
export function simulateAssistantMail(
  input: z.input<typeof SimulateAssistantMail>,
): Promise<SimulatedMailResult> {
  return request('/api/v1/admin/assistant-mail', {
    method: 'POST',
    body: SimulateAssistantMail.parse(input),
    schema: SimulatedMailResult,
  });
}

/** Whether Help chat and Ask the data take questions for me right now. */
export const aiStatusQuery = queryOptions({
  queryKey: aiKeys.status,
  queryFn: ({ signal }) => request('/api/v1/ai/status', { schema: AiStatus, signal }),
  staleTime: 60_000,
});

// ---- Admin › AI ---------------------------------------------------------------------------

export const aiAdminQuery = queryOptions({
  queryKey: aiKeys.admin,
  queryFn: ({ signal }) => request('/api/v1/admin/ai', { schema: AiAdminView, signal }),
  staleTime: 5_000,
});

/**
 * Test connection (APP-107 §2.2): tests exactly what is on the card and saves nothing. Called
 * directly: a typed key goes from the field to this call and nowhere else, never the query cache.
 */
export function testAiProvider(input: TestAiProvider): Promise<AiProviderTestResult> {
  return request('/api/v1/admin/ai/provider/test', {
    method: 'POST',
    body: TestAiProvider.parse(input),
    schema: AiProviderTestResult,
  });
}

/** Save: the server runs the same test first and changes nothing unless it passes. */
export function saveAiProvider(input: SaveAiProvider): Promise<AiAdminView> {
  return request('/api/v1/admin/ai/provider', {
    method: 'PUT',
    body: SaveAiProvider.parse(input),
    schema: AiAdminView,
  });
}

/** Test speech (APP-107 §2.4): a built-in one-second clip; nothing to send. */
export function testAiSpeech(): Promise<AiSpeechTestResult> {
  return request('/api/v1/admin/ai/speech/test', {
    method: 'POST',
    body: {},
    schema: AiSpeechTestResult,
  });
}

/** The settings Admin › AI writes through Settings (audited, with History): `api/settings.ts`. */
export type AiSettingKey =
  | 'ai.enabled'
  | 'ai.provider'
  | 'ai.defaultModel'
  | 'ai.features'
  | 'ai.dailyLimit'
  | 'ai.historyDays'
  | 'ai.askDataHistoryMonths'
  | 'help.teamEmail';

/** Key saves and connection tests, from the audit log (never the key). */
export function aiAuditQuery(entityType: 'ai_credential' | 'ai_connection_test') {
  return queryOptions({
    // The speech and model tests share one audit type; History filters them apart on screen.
    queryKey: aiKeys.audit(entityType),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/audit?entityType=${entityType}&size=50`, {
        schema: AuditLogPage,
        signal,
      }),
    staleTime: 5_000,
  });
}

// ---- Help ---------------------------------------------------------------------------------

export function helpTopicsQuery(page: string) {
  return queryOptions({
    queryKey: aiKeys.topics(page),
    queryFn: ({ signal }) =>
      request(`/api/v1/help/topics?page=${encodeURIComponent(page)}`, {
        schema: HelpTopics,
        signal,
      }),
    staleTime: 0,
  });
}

/** Search every help topic I may see (no AI). */
export function searchHelp(q: string, signal?: AbortSignal): Promise<HelpSearchResults> {
  return request(`/api/v1/help/search?q=${encodeURIComponent(q)}`, {
    schema: HelpSearchResults,
    ...(signal !== undefined && { signal }),
  });
}

export const helpConversationQuery = queryOptions({
  queryKey: aiKeys.conversation,
  queryFn: ({ signal }) =>
    request('/api/v1/help/conversation', { schema: HelpConversation, signal }),
  staleTime: 30_000,
});

export function askHelp(question: string, page: string): Promise<HelpReply> {
  return request('/api/v1/help/ask', {
    method: 'POST',
    body: { question, page },
    schema: HelpReply,
  });
}

/** A one-use ticket for dictating a question (APP-124); null when voice isn't offered now. */
export function helpVoiceTicket(): Promise<HelpVoiceTicket> {
  return request('/api/v1/help/voice', { method: 'POST', body: {}, schema: HelpVoiceTicket });
}

export function rateHelpAnswer(
  messageId: string,
  rating: AiFeedback['rating'],
): Promise<HelpMessage> {
  return request(`/api/v1/help/messages/${encodeURIComponent(messageId)}/feedback`, {
    method: 'POST',
    body: { rating },
    schema: HelpMessage,
  });
}

export function clearHelpHistory(): Promise<HelpConversation> {
  return request('/api/v1/help/conversation', { method: 'DELETE', schema: HelpConversation });
}

export function sendHelpHandoff(input: HelpHandoff): Promise<HelpHandoffSent> {
  return request('/api/v1/help/handoff', {
    method: 'POST',
    body: input,
    schema: HelpHandoffSent,
  });
}

// ---- Ask the data -------------------------------------------------------------------------

export const askStartersQuery = queryOptions({
  queryKey: aiKeys.starters,
  queryFn: ({ signal }) =>
    request('/api/v1/insights/ask/starters', { schema: AskDataStarters, signal }),
  staleTime: 60_000,
});

/** A report re-run without AI: a starter, a chip change or a saved question. */
export function askRunQuery(query: AskDataQuery) {
  return queryOptions({
    queryKey: aiKeys.run(query),
    queryFn: ({ signal }) =>
      request('/api/v1/insights/ask/run', {
        method: 'POST',
        body: { query },
        schema: AskDataResult,
        signal,
      }),
    staleTime: 60_000,
  });
}

/**
 * Ask a question. A follow-up continues the conversation; a new conversation started from a
 * project's board names the project, so its first question starts from the project's default
 * scope (APP-191 D9).
 */
export function askData(
  question: string,
  conversationId: string | null,
  projectId: string | null = null,
): Promise<AskDataAnswer> {
  return request('/api/v1/insights/ask', {
    method: 'POST',
    body: { question, conversationId, ...(projectId !== null && { projectId }) },
    schema: AskDataAnswer,
  });
}

// APP-191 slice 3: an answer is saved by pinning it to a project (`api/askKeep.ts`); the saved
// questions from before are the "My reports" project's board.

export function rateAskAnswer(
  messageId: string,
  rating: AiFeedback['rating'],
): Promise<AiFeedbackSaved> {
  return request(`/api/v1/insights/ask/messages/${encodeURIComponent(messageId)}/feedback`, {
    method: 'POST',
    body: { rating },
    schema: AiFeedbackSaved,
  });
}

/** The files an answer exports to (APP-191 D7). */
export type AskExportFormat = AskDataExportFormat;

const EXPORT_TYPE: Readonly<Record<AskExportFormat, string>> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

/**
 * The answer as a file, re-run on the server from its query (audited and limited there): the Excel
 * workbook or the CSV (APP-257's export, `AskDataExport`: the query and the format, nothing
 * else). The file comes with its own name ("MyRewards · Value by sector · FY2026 to
 * 2026-10-14.xlsx"); `fileName` is null if the server gave none.
 */
export async function exportAsk(
  query: AskDataQuery,
  format: AskExportFormat,
): Promise<{ blob: Blob; fileName: string | null }> {
  let fileName: string | null = null;
  const body: AskDataExport = { query, format };
  const blob = await requestDownload('/api/v1/insights/ask/export', {
    method: 'POST',
    body,
    contentType: EXPORT_TYPE[format],
    maxBytes: 10 * 1024 * 1024,
    onFileName: (name) => {
      fileName = name;
    },
  });
  return { blob, fileName };
}

/**
 * Every row of an answer for its table on the page (APP-266, D14), up to 1,000, worked out now in
 * the asker's scope. Only the table comes back, and only the table holds it.
 */
export function askAllRows(query: AskDataQuery): Promise<AskDataTable> {
  return request('/api/v1/insights/ask/rows', {
    method: 'POST',
    body: { query },
    schema: AskDataTable,
  });
}

/** Remember the view an answer was switched to, on the asker's own message (D6). */
export function setAskView(messageId: string, view: AskView): Promise<AskDataViewSaved> {
  return request(`/api/v1/insights/ask/messages/${encodeURIComponent(messageId)}/view`, {
    method: 'PATCH',
    body: { view },
    schema: AskDataViewSaved,
  });
}

/** Record a chart image downloaded or copied, before it's made (D7). */
export function recordAskImage(
  query: AskDataQuery,
  view: AskView,
  kind: AskDataImageKind,
): Promise<AskDataImageRecorded> {
  return request('/api/v1/insights/ask/image-audit', {
    method: 'POST',
    body: { query, view, kind },
    schema: AskDataImageRecorded,
  });
}
